/**
 * ContractorDetail — one contractor, everything about them.
 *
 * The screen that makes the master worth maintaining: compliance, workers with
 * their eligibility, site assignments and documents in one place, so registering
 * a contractor properly has a visible payoff.
 *
 * Compliance percentages come from the SAME evaluation the permit gate uses
 * (compliance_breakdown reads the eligibility rules), so a contractor shown here
 * as complete cannot then be refused at issue for a reason this page did not
 * count.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Building2, Users, FileText, ShieldCheck, MapPin, Loader2,
  Plus, Trash2, Download, Upload, AlertTriangle,
} from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import EligibilityBadge from '../../components/EligibilityBadge';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import {
  fetchContractors, fetchWorkers, fetchContractorCompliance,
  fetchBulkEligibility, fetchAssignments, fetchContractorTypes,
  saveAssignment, deleteAssignment,
  fetchContractorDocuments, uploadContractorDocument,
  contractorDocumentDownloadUrl, deleteContractorDocument,
} from '../../services/contractorApi';

const TABS = [
  { key: 'overview', label: 'Overview', Icon: Building2 },
  { key: 'workers', label: 'Workers', Icon: Users },
  { key: 'compliance', label: 'Compliance', Icon: ShieldCheck },
  { key: 'sites', label: 'Site assignments', Icon: MapPin },
  { key: 'documents', label: 'Documents', Icon: FileText },
];

function Percent({ value }) {
  // null means "nothing to assess", which is not 0% — saying 0 would read as a
  // failure where the truth is that the question does not apply yet.
  if (value === null || value === undefined) {
    return <span className="text-xs text-gray-400">Not applicable</span>;
  }
  const tone = value >= 100 ? 'text-emerald-700'
    : value >= 60 ? 'text-amber-700' : 'text-red-700';
  return (
    <span className={`text-sm font-semibold tabular-nums ${tone}`}>
      {value}%
    </span>
  );
}

export default function ContractorDetail({ user, onLogout }) {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess, confirm } = useAlert();
  const { activeSiteId, availableSites } = useSite();

  const [tab, setTab] = useState('overview');
  const [contractor, setContractor] = useState(null);
  const [types, setTypes] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [eligibility, setEligibility] = useState({});
  const [compliance, setCompliance] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [docCategories, setDocCategories] = useState({});
  const [busy, setBusy] = useState(false);
  // Server-reported, not inferred from the role name. The server enforces the
  // permission regardless; this only decides whether to offer controls that
  // would fail.
  const [canManage, setCanManage] = useState(false);
  // Add-assignment row state, kept out of the table so an in-progress entry
  // survives a re-render of the list.
  const [newAssignment, setNewAssignment] = useState(
    { site_id: '', work_categories: '', valid_to: '' });

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [cRes, wRes, tRes] = await Promise.all([
        fetchContractors({ signal }),
        fetchWorkers({}, { signal }),
        fetchContractorTypes({ signal }).catch(() => ({ types: [] })),
      ]);
      const found = (cRes.companies || []).find((c) => c.id === contractorId);
      setContractor(found || null);
      setTypes(tRes.types || []);

      const mine = (wRes.workers || [])
        .filter((w) => w.contractor_company_id === contractorId);
      setWorkers(mine);

      // Compliance and eligibility are separate round trips because most visits
      // never leave the Overview tab; they are cheap enough to fetch together
      // and expensive enough not to fetch per worker.
      const [compRes, eligRes, asgRes] = await Promise.all([
        fetchContractorCompliance(contractorId, { siteId: activeSiteId }, { signal })
          .catch(() => null),
        mine.length
          ? fetchBulkEligibility({
            workerIds: mine.map((w) => w.id), siteId: activeSiteId,
          }, { signal }).catch(() => ({ eligibility: {} }))
          : Promise.resolve({ eligibility: {} }),
        fetchAssignments({ contractorId }, { signal }).catch(() => ({ assignments: [] })),
      ]);
      setCompliance(compRes);
      setEligibility(eligRes.eligibility || {});
      setAssignments(asgRes.assignments || []);
      setCanManage(Boolean(asgRes.can_manage));

      // Which documents this contractor's TYPE requires, so the tab can show
      // what is outstanding rather than only what happens to be uploaded.
      const myType = (tRes.types || [])
        .find((t) => t.id === (found?.contractor_type_id))
        || (tRes.types || []).find((t) => t.is_default);
      setRequiredDocs(myType?.required_documents || []);
      setDocCategories(tRes.document_categories || {});

      const docRes = await fetchContractorDocuments(contractorId, { signal })
        .catch(() => ({ files: [] }));
      setDocuments(docRes.files || []);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      showError('Could not load the contractor.');
    } finally {
      setLoading(false);
    }
  }, [contractorId, activeSiteId, showError]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => {
    const controller = new AbortController();
    load(controller.signal);
  }, [load]);

  const addAssignment = async () => {
    if (!newAssignment.site_id) {
      showError('Choose a site.');
      return;
    }
    setBusy(true);
    try {
      await saveAssignment({
        contractor_company_id: contractorId,
        site_id: newAssignment.site_id,
        // Comma-separated in the form, a list on the wire. An empty list means
        // "any work", which the eligibility rules treat as unrestricted.
        work_categories: newAssignment.work_categories
          .split(',').map((x) => x.trim()).filter(Boolean),
        valid_to: newAssignment.valid_to || null,
      });
      setNewAssignment({ site_id: '', work_categories: '', valid_to: '' });
      showSuccess('Site assignment saved.');
      reload();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Could not save the assignment.');
    } finally {
      setBusy(false);
    }
  };

  const removeAssignment = async (assignment) => {
    const ok = await confirm(
      'End this site assignment?',
      `${contractor?.name} will no longer be engaged at `
      + `${assignment.site_name || 'this site'}. Their workers cannot be `
      + 'issued a permit there until it is reinstated.',
      { confirmLabel: 'End assignment', destructive: true },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await deleteAssignment(assignment.id);
      showSuccess('Assignment ended.');
      reload();
    } catch (err) {
      showError('Could not end the assignment.');
    } finally {
      setBusy(false);
    }
  };

  const uploadDocument = async (documentType, file) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadContractorDocument(contractorId, file, { documentType });
      showSuccess(`${docCategories[documentType] || documentType} uploaded.`);
      reload();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const openDocument = async (fileId) => {
    try {
      const url = await contractorDocumentDownloadUrl(fileId);
      if (url) window.open(url, '_blank', 'noopener');
      else showError('No download link was returned for this document.');
    } catch (err) {
      showError('Could not open the document.');
    }
  };

  const removeDocument = async (doc) => {
    const ok = await confirm(
      'Delete this document?',
      `${doc.original_filename || doc.filename} will be removed. If a `
      + 'requirement relied on it, the contractor may stop being compliant.',
      { confirmLabel: 'Delete', destructive: true },
    );
    if (!ok) return;
    setBusy(true);
    try {
      await deleteContractorDocument(doc.id);
      showSuccess('Document deleted.');
      reload();
    } catch (err) {
      showError('Could not delete the document.');
    } finally {
      setBusy(false);
    }
  };

  // Document type -> the newest upload carrying that tag. Matches what
  // contractor_eligibility reads, so this tab and the gate agree.
  const docByType = {};
  (documents || []).forEach((d) => {
    (d.tags || []).forEach((t) => {
      const key = String(t).trim();
      if (key && !docByType[key]) docByType[key] = d;
    });
  });

  const typeName = types.find((t) => t.id === contractor?.contractor_type_id)?.name;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center text-gray-500">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          <p className="mt-2 text-sm">Loading contractor…</p>
        </div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <p className="text-gray-600">
            This contractor does not exist, or you do not have access to it.
          </p>
          <button onClick={() => navigate('/contractors')}
                  className="mt-3 text-sm font-medium text-blue-600 hover:underline">
            Back to contractors
          </button>
        </div>
      </div>
    );
  }

  const eligibleCount = Object.values(eligibility).filter((e) => e.eligible).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <button onClick={() => navigate('/contractors')}
                    className="mb-2 inline-flex items-center gap-1 text-sm
                               text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-4 w-4" /> Contractors
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{contractor.name}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              {typeName && (
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium">
                  {typeName}
                </span>
              )}
              <span className={`rounded px-2 py-0.5 text-xs font-medium ${
                (contractor.status || '').toLowerCase() === 'active'
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-amber-100 text-amber-800'}`}>
                {contractor.status || 'unset'}
              </span>
              {contractor.registration_no && (
                <span className="text-xs text-gray-500">
                  Reg. {contractor.registration_no}
                </span>
              )}
            </p>
          </div>
          {compliance && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-gray-500">
                Overall compliance
              </p>
              <p className="text-3xl font-bold tabular-nums text-gray-900">
                {compliance.overall_percent === null
                  ? '—' : `${compliance.overall_percent}%`}
              </p>
              <p className="text-xs text-gray-500">
                {eligibleCount} of {workers.length} worker
                {workers.length === 1 ? '' : 's'} eligible
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-5 flex flex-wrap gap-1 border-b border-gray-200">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium
                          ${tab === key
                            ? 'border-b-2 border-blue-600 text-blue-700'
                            : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {/* ---- Overview ---- */}
        {tab === 'overview' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-900">Contact</h2>
              <dl className="space-y-2 text-sm">
                {[['Contact person', contractor.contact_person],
                  ['Email', contractor.contact_email],
                  ['Phone', contractor.contact_phone],
                  ['Address', contractor.address]].map(([k, v]) => (
                    <div key={k} className="flex gap-3">
                      <dt className="w-32 flex-shrink-0 text-gray-500">{k}</dt>
                      <dd className="text-gray-900">{v || '—'}</dd>
                    </div>
                ))}
              </dl>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-900">Commercial validity</h2>
              <dl className="space-y-2 text-sm">
                {[['Insurance', contractor.insurance_expiry],
                  ["Workmen's comp.", contractor.wc_policy_expiry],
                  ['ESIC', contractor.esic_expiry],
                  ['Agreement', contractor.agreement_expiry]].map(([k, v]) => (
                    <div key={k} className="flex gap-3">
                      <dt className="w-32 flex-shrink-0 text-gray-500">{k}</dt>
                      <dd className="text-gray-900">
                        {v ? String(v).slice(0, 10) : '—'}
                      </dd>
                    </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {/* ---- Workers ---- */}
        {tab === 'workers' && (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            {workers.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">
                No workers registered for this contractor yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="border-b border-gray-200 text-[11px]
                                    uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Worker</th>
                      <th className="px-4 py-2 font-semibold">Trade</th>
                      <th className="px-4 py-2 font-semibold">Badge</th>
                      <th className="px-4 py-2 font-semibold">Eligibility</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map((w) => (
                      <tr key={w.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2">
                          <button
                            onClick={() => navigate(`/contractors/workers/${w.id}`)}
                            className="font-medium text-blue-700 hover:underline"
                          >
                            {w.name}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-gray-600">{w.trade || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{w.badge_no || '—'}</td>
                        <td className="px-4 py-2">
                          <EligibilityBadge result={eligibility[w.id]} compact />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ---- Compliance ---- */}
        {tab === 'compliance' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            {!compliance ? (
              <p className="text-sm text-gray-500">
                Compliance could not be evaluated.
              </p>
            ) : (
              <>
                <p className="mb-4 text-xs text-gray-500">
                  Computed from the same rules the permit gate applies, so a
                  section shown as complete here cannot be refused at issue for a
                  reason this page did not count.
                </p>
                <ul className="divide-y divide-gray-100">
                  {compliance.sections.map((s) => (
                    <li key={s.key} className="flex items-center justify-between py-2.5">
                      <span className="text-sm text-gray-900">{s.label}</span>
                      <Percent value={s.percent} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* ---- Site assignments ---- */}
        {tab === 'sites' && (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-gray-200
                            bg-white shadow-sm">
              {assignments.length === 0 ? (
                <p className="p-8 text-center text-sm text-gray-500">
                  This contractor is not assigned to any site. Without an
                  assignment their workers cannot be issued a permit at a site.
                </p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-200 text-[11px]
                                    uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Site</th>
                      <th className="px-4 py-2 font-semibold">Work categories</th>
                      <th className="px-4 py-2 font-semibold">Valid to</th>
                      <th className="px-4 py-2 font-semibold">Status</th>
                      {canManage && <th className="px-4 py-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => (
                      <tr key={a.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-4 py-2 text-gray-900">
                          {a.site_name || a.site_id}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {(a.work_categories || []).length
                            ? a.work_categories.join(', ')
                            : <span className="text-gray-400">Any work</span>}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {a.valid_to ? String(a.valid_to).slice(0, 10) : '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{a.status}</td>
                        {canManage && (
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => removeAssignment(a)}
                              className="inline-flex items-center gap-1 text-xs
                                         font-medium text-red-600 hover:underline
                                         disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> End
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {canManage && (
              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800">
                  Engage at a site
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Assigning the same site again updates the existing engagement
                  rather than creating a duplicate.
                </p>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700"
                           htmlFor="asg-site">
                      Site
                    </label>
                    <select
                      id="asg-site"
                      value={newAssignment.site_id}
                      onChange={(e) => setNewAssignment(
                        (v) => ({ ...v, site_id: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2
                                 text-sm focus:border-transparent focus:outline-none
                                 focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="">— Select —</option>
                      {(availableSites || []).map((s) => (
                        <option key={s.site_id} value={s.site_id}>
                          {s.site_name || s.site_id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-semibold text-gray-700"
                           htmlFor="asg-cats">
                      Work categories{' '}
                      <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="asg-cats"
                      type="text"
                      value={newAssignment.work_categories}
                      placeholder="Hot Work, Electrical — leave blank for any work"
                      onChange={(e) => setNewAssignment(
                        (v) => ({ ...v, work_categories: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2
                                 text-sm focus:border-transparent focus:outline-none
                                 focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700"
                           htmlFor="asg-to">
                      Valid to{' '}
                      <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="asg-to"
                      type="date"
                      value={newAssignment.valid_to}
                      onChange={(e) => setNewAssignment(
                        (v) => ({ ...v, valid_to: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2
                                 text-sm focus:border-transparent focus:outline-none
                                 focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addAssignment}
                  disabled={busy || !newAssignment.site_id}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg
                             bg-[#1B5C94] px-3.5 py-2 text-sm font-medium text-white
                             hover:bg-[#164b79] disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Plus className="h-4 w-4" />}
                  Save assignment
                </button>
              </div>
            )}
          </div>
        )}

        {/* ---- Documents ---- */}
        {tab === 'documents' && (
          <div className="space-y-4">
            {/* What the requirement template asks for, and whether it is here.
                Driven by the SAME tag eligibility reads, so this list and the
                permit gate cannot disagree about what is on file. */}
            <div className="overflow-hidden rounded-xl border border-gray-200
                            bg-white shadow-sm">
              <div className="border-b border-gray-200 px-5 py-3">
                <h3 className="text-sm font-bold text-gray-800">
                  Required documents
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {typeName
                    ? `From the ${typeName} requirement template.`
                    : 'From the default requirement template.'}
                </p>
              </div>
              {requiredDocs.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-500">
                  This contractor’s type asks for no documents. Anything uploaded
                  below is still kept against the contractor.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {requiredDocs.map((key) => {
                    const doc = docByType[key];
                    return (
                      <li key={key}
                          className="flex flex-wrap items-center justify-between
                                     gap-3 px-5 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {docCategories[key] || key}
                          </p>
                          {doc ? (
                            <p className="mt-0.5 truncate text-xs text-gray-500">
                              {doc.original_filename || doc.filename}
                              {doc.created_at
                                && ` · uploaded ${String(doc.created_at).slice(0, 10)}`}
                            </p>
                          ) : (
                            <p className="mt-0.5 flex items-center gap-1 text-xs
                                          text-amber-700">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              Not on file
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {doc && (
                            <button
                              type="button"
                              onClick={() => openDocument(doc.id)}
                              className="inline-flex items-center gap-1 text-xs
                                         font-medium text-blue-600 hover:underline"
                            >
                              <Download className="h-3.5 w-3.5" /> Open
                            </button>
                          )}
                          {canManage && (
                            <label className="inline-flex cursor-pointer items-center
                                              gap-1 rounded-lg border border-gray-300
                                              px-2.5 py-1.5 text-xs font-medium
                                              text-gray-700 hover:bg-gray-50">
                              <Upload className="h-3.5 w-3.5" />
                              {doc ? 'Replace' : 'Upload'}
                              <input
                                type="file"
                                className="hidden"
                                disabled={busy}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  // Clear the input so re-picking the same file
                                  // fires change again.
                                  e.target.value = '';
                                  uploadDocument(key, f);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Everything actually attached, including types the template does
                not ask for. */}
            <div className="overflow-hidden rounded-xl border border-gray-200
                            bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3
                              border-b border-gray-200 px-5 py-3">
                <h3 className="text-sm font-bold text-gray-800">
                  All documents{documents.length ? ` (${documents.length})` : ''}
                </h3>
                {canManage && (
                  <label className="inline-flex cursor-pointer items-center gap-1.5
                                    rounded-lg bg-[#1B5C94] px-3 py-1.5 text-xs
                                    font-medium text-white hover:bg-[#164b79]">
                    <Upload className="h-3.5 w-3.5" /> Upload other
                    <input
                      type="file"
                      className="hidden"
                      disabled={busy}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        uploadDocument('other', f);
                      }}
                    />
                  </label>
                )}
              </div>
              {documents.length === 0 ? (
                <p className="p-6 text-center text-sm text-gray-500">
                  No documents attached yet.
                </p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-200 text-[11px]
                                    uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-semibold">File</th>
                      <th className="px-4 py-2 font-semibold">Type</th>
                      <th className="px-4 py-2 font-semibold">Uploaded</th>
                      <th className="px-4 py-2 font-semibold">By</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((d) => (
                      <tr key={d.id} className="border-b border-gray-50 last:border-0">
                        <td className="max-w-xs truncate px-4 py-2 text-gray-900">
                          {d.original_filename || d.filename}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {(d.tags || []).length
                            ? (d.tags || [])
                              .map((t) => docCategories[t] || t).join(', ')
                            : <span className="text-gray-400">Untyped</span>}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {d.created_at ? String(d.created_at).slice(0, 10) : '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {d.uploaded_by_name || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => openDocument(d.id)}
                            className="mr-3 inline-flex items-center gap-1 text-xs
                                       font-medium text-blue-600 hover:underline"
                          >
                            <Download className="h-3.5 w-3.5" /> Open
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => removeDocument(d)}
                              className="inline-flex items-center gap-1 text-xs
                                         font-medium text-red-600 hover:underline
                                         disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="text-xs text-gray-500">
              Contractor documents are ordinary platform uploads attached to this
              contractor, so they inherit virus scanning and the audit trail
              rather than living in a contractor-only store. The four commercial
              validity dates on the Overview tab remain the fast path; an upload
              adds the evidence behind them.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
